// NewTrip — Phase 4 Plan 04-03 (DIS-07, D-43, D-75); migrated to telegram-ui in
// Phase 12 Plan 12-07 (UI-SPEC §7 Wave 2, RegistrationScreen form pattern).
//
// Multi-stop trip create form with:
//   - 2-20 stops (drag-reorder via @dnd-kit/sortable) — logic PRESERVED.
//   - HERE Places Autosuggest per stop (D-75) with frozen geocoding.
//   - role auto-assignment by position (D-75).
//   - RecipientsFieldset wire-up (D-43 carryover from Phase 3).
//   - Two save actions: "Save trip" (no notify) and "Save and notify recipients"
//     (notify_recipients=true). The notify action is the native MainButton
//     primary CTA «Создать рейс»; "Save without notify" is a secondary Button.
//
// telegram-ui migration (UI-SPEC §7 / §222):
//   - <Steps> wizard indicator; <Section> driver (<Avatar> + <Cell>);
//     <Section> stops (drag preserved); <Section> recipients;
//     native MainButton (sdk-react, Pitfall 5: setLoading+disabled+cleanup);
//     native BackButton (nested screen).
//   - data-testid preserved (telegram-ui forwards ...restProps) so Phase 4
//     tests survive (RESEARCH Pitfall 6).
//   - No hardcoded hex / no inline font overrides — telegram-ui themes the surface.
//
// Threat model (T-12-07-01/02): zod-mirror validation kept verbatim (2 ≤ stops ≤
// 20, ≥1 recipient, ≥1 valid lat/lng); MainButton loader + disabled guard the
// double-submit (Pitfall 5).

import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useMutation } from "@tanstack/react-query";
import {
  Avatar,
  Banner,
  Button,
  Cell,
  Input,
  List,
  Section,
  Steps,
} from "@telegram-apps/telegram-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  type StopRole,
  type TripCreatePayload,
  type TripCreateResponse,
  tripsApi,
} from "../api/trips";
import { useAuth } from "../auth/useAuth";
import { DriverSelect } from "../components/DriverSelect";
import { type RecipientDraft, RecipientsFieldset } from "../components/RecipientsFieldset";
import type { StopAddress } from "../components/StopInput";
import { StopRow, type StopRowData } from "../components/StopRow";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";
import { isMainButtonAvailable, useBackButton, useMainButton } from "../lib/telegramButtons";

const MIN_STOPS = 2;
const MAX_STOPS = 20;

// 3-step wizard (UI-SPEC §7 NewTrip Steps): driver → stops → recipients.
const WIZARD_STEPS = 3;

function _emptyAddress(): StopAddress {
  return { address: "", lat: null, lng: null, formatted_address: null };
}

function _emptyRow(): StopRowData {
  // crypto.randomUUID has good support — fallback to Date+Math.random.
  let id: string;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    id = crypto.randomUUID();
  } else {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return {
    id,
    address: _emptyAddress(),
    cargo_description: "",
    planned_arrival_window_start: "",
    planned_arrival_window_end: "",
  };
}

function _assignRole(index: number, total: number): StopRole {
  if (index === 0) return "pickup";
  if (index === total - 1) return "dropoff";
  return "intermediate";
}

export function NewTrip(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const [stops, setStops] = useState<StopRowData[]>(() => [_emptyRow(), _emptyRow()]);
  const [driverId, setDriverId] = useState<number | null>(null);
  const [externalId, setExternalId] = useState<string>("");
  const [recipients, setRecipients] = useState<RecipientDraft[]>([
    {
      recipient_email: "",
      recipient_locale: "en",
      recipient_telegram_username: null,
      segment_filter_stop_ids: [],
    },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<TripCreateResponse | null>(null);

  // Native back navigation on this nested screen (UI-SPEC §Platform Contract).
  useBackButton(() => navigate(-1));
  // Native MainButton present? Then suppress the in-DOM primary-CTA duplicate.
  const nativeMainButton = useMemo(() => isMainButtonAvailable(), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const createMutation = useMutation({
    mutationFn: (payload: TripCreatePayload) => tripsApi.create(apiClient, payload),
    onSuccess: (data) => {
      setSuccessData(data);
      setError(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    },
  });

  function handleDragEnd(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setStops((prev) => {
      const oldIdx = prev.findIndex((r) => r.id === active.id);
      const newIdx = prev.findIndex((r) => r.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }

  function addStop(): void {
    setStops((prev) => (prev.length >= MAX_STOPS ? prev : [...prev, _emptyRow()]));
  }

  function deleteStop(index: number): void {
    setStops((prev) => (prev.length <= MIN_STOPS ? prev : prev.filter((_, i) => i !== index)));
  }

  function updateStop(index: number, next: StopRowData): void {
    setStops((prev) => prev.map((s, i) => (i === index ? next : s)));
  }

  const availableStopOptions = useMemo(
    () =>
      stops.map((s, i) => ({
        id: i,
        label:
          s.address.formatted_address?.trim() ||
          s.address.address.trim() ||
          (t("dispatcher:newtrip.stop_unset", "Stop {{n}}", { n: i + 1 }) as string),
      })),
    [stops, t],
  );

  function buildPayload(notify: boolean): TripCreatePayload | string {
    if (stops.length < MIN_STOPS || stops.length > MAX_STOPS) {
      return t("dispatcher:newtrip.error_stops_count", {
        defaultValue: "Trip must have between 2 and 20 stops",
      }) as string;
    }
    if (recipients.length < 1) {
      return t("dispatcher:newtrip.error_no_recipients", "Add at least one recipient") as string;
    }
    for (let i = 0; i < stops.length; i++) {
      const s = stops[i];
      if (!s) continue;
      if (s.address.lat === null || s.address.lng === null || s.address.address.trim() === "") {
        return t("dispatcher:newtrip.error_stop_missing", {
          defaultValue: "Stop {{n}} is missing address or coordinates",
          n: i + 1,
        }) as string;
      }
    }
    for (const r of recipients) {
      if (!r.recipient_email.trim() || !r.recipient_email.includes("@")) {
        return t(
          "dispatcher:newtrip.error_recipient_email",
          "Recipient email is missing or invalid",
        ) as string;
      }
    }
    return {
      driver_id: driverId,
      external_id: externalId.trim() || null,
      stops: stops.map((s, i) => ({
        address: s.address.address,
        lat: s.address.lat ?? 0,
        lng: s.address.lng ?? 0,
        formatted_address: s.address.formatted_address ?? null,
        cargo_description: s.cargo_description.trim() || null,
        planned_arrival_window_start: s.planned_arrival_window_start || null,
        planned_arrival_window_end: s.planned_arrival_window_end || null,
        role: _assignRole(i, stops.length),
      })),
      recipients: recipients.map((r) => ({
        recipient_email: r.recipient_email.trim(),
        recipient_locale: r.recipient_locale,
        recipient_telegram_username: r.recipient_telegram_username || null,
        segment_filter_stop_ids: r.segment_filter_stop_ids ?? [],
      })),
      notify_recipients: notify,
    };
  }

  function onSubmit(notify: boolean): void {
    if (createMutation.isPending) return; // double-submit guard (Pitfall 5)
    const built = buildPayload(notify);
    if (typeof built === "string") {
      setError(built);
      return;
    }
    setError(null);
    createMutation.mutate(built);
  }

  // Wizard progress: 0 = driver picked? then stops, then recipients valid.
  const wizardProgress = useMemo(() => {
    let p = 0;
    if (driverId !== null) p = 1;
    const stopsComplete = stops.every(
      (s) => s.address.lat !== null && s.address.lng !== null && s.address.address.trim() !== "",
    );
    if (p >= 1 && stopsComplete) p = 2;
    if (p >= 2 && recipients.some((r) => r.recipient_email.includes("@"))) p = 3;
    return p;
  }, [driverId, stops, recipients]);

  // Native MainButton primary CTA «Создать рейс» (= save + notify). One source
  // per screen (UI-SPEC §6); loader + disabled guard double-tap (Pitfall 5).
  useMainButton({
    text: t("dispatcher:newtrip.save_and_notify", "Save and notify recipients") as string,
    onClick: () => onSubmit(true),
    loading: createMutation.isPending,
    enabled: !createMutation.isPending,
    visible: successData === null,
  });

  if (successData) {
    return (
      <List data-testid="newtrip-success">
        <Section header={t("dispatcher:newtrip.success_heading", "Trip created") as string}>
          <Cell multiline>
            {t("dispatcher:newtrip.success_summary", {
              defaultValue:
                "Trip #{{trip_id}} created with {{stops}} stops and {{recipients}} recipient(s).",
              trip_id: successData.trip_id,
              stops: successData.stop_count,
              recipients: successData.recipient_count,
            })}
          </Cell>
        </Section>
        <Section header={t("dispatcher:newtrip.tracker_urls_heading", "Tracker URLs") as string}>
          {successData.tracker_urls.map((u) => (
            <Cell
              key={u.invite_id}
              data-testid={`tracker-url-${u.invite_id}`}
              subtitle={u.tracker_url}
              multiline
            >
              {u.recipient_email}
            </Cell>
          ))}
        </Section>
        <Section>
          <Cell
            Component="button"
            onClick={() => navigate(`/dispatcher/trips/${successData.trip_id}`)}
            data-testid="newtrip-go-to-trip"
          >
            {t("dispatcher:newtrip.go_to_trip", "Go to trip")}
          </Cell>
        </Section>
      </List>
    );
  }

  const driverLabel =
    driverId !== null
      ? (t("dispatcher:newtrip.driver_selected", "Driver #{{id}}", { id: driverId }) as string)
      : (t("dispatcher:newtrip.driver_unassigned", "No driver assigned") as string);

  return (
    <List data-testid="newtrip">
      <div data-testid="newtrip-steps">
        <Steps count={WIZARD_STEPS} progress={wizardProgress} />
      </div>

      <Section
        data-testid="newtrip-driver-section"
        header={t("dispatcher:newtrip.driver_label", "Driver") as string}
      >
        <Cell
          data-testid="newtrip-driver-cell"
          before={<Avatar size={40} acronym={driverId !== null ? `#${driverId}` : "?"} />}
          multiline
        >
          {driverLabel}
        </Cell>
        <DriverSelect value={driverId} onChange={(id) => setDriverId(id)} />
        <Input
          header={
            t("dispatcher:newtrip.external_id_label", "External reference (optional)") as string
          }
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder={t("dispatcher:newtrip.external_id_placeholder", "ORD-12345") as string}
          data-testid="newtrip-external-id"
        />
      </Section>

      <Section header={t("dispatcher:newtrip.stops_heading", "Stops") as string}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={stops.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {stops.map((row, idx) => (
              <StopRow
                key={row.id}
                row={row}
                index={idx}
                total={stops.length}
                role={_assignRole(idx, stops.length)}
                onChange={(next) => updateStop(idx, next)}
                onDelete={() => deleteStop(idx)}
                canDelete={stops.length > MIN_STOPS}
                locale={i18n.resolvedLanguage}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Cell
          Component="button"
          onClick={addStop}
          interactiveAnimation="opacity"
          data-testid="newtrip-add-stop"
          aria-disabled={stops.length >= MAX_STOPS}
        >
          + {t("dispatcher:newtrip.add_stop", "Add stop")}
        </Cell>

        {stops.length >= 4 && (
          <Cell
            Component="button"
            onClick={() => {
              // HERE Optimize — placeholder per RESEARCH §Open Q2 + CONTEXT §Specifics.
              setError(
                t(
                  "dispatcher:newtrip.optimize_unavailable",
                  "Order optimization is unavailable in this release.",
                ) as string,
              );
            }}
            data-testid="newtrip-optimize"
          >
            {t("dispatcher:newtrip.optimize", "Optimize order (HERE)")}
          </Cell>
        )}
      </Section>

      <Section header={t("dispatcher:newtrip.recipients_heading", "Recipients") as string}>
        <RecipientsFieldset
          value={recipients}
          onChange={setRecipients}
          availableStops={availableStopOptions}
        />
      </Section>

      {error ? (
        <Banner
          type="section"
          data-testid="newtrip-error"
          role="alert"
          header={t("dispatcher:newtrip.error_heading", "Cannot create trip") as string}
        >
          {error}
        </Banner>
      ) : null}

      <Section>
        {/* Primary "Save and notify" CTA is the native Telegram MainButton
            (UI-SPEC §87). Render the in-DOM duplicate ONLY when the native bar
            is unavailable (web/desktop, tests) — otherwise both showed at once
            (Phase 12 UAT 2026-06-16). */}
        {!nativeMainButton ? (
          <Cell
            Component="button"
            onClick={() => onSubmit(true)}
            data-testid="newtrip-mainbutton"
            aria-disabled={createMutation.isPending}
          >
            {t("dispatcher:newtrip.save_and_notify", "Save and notify recipients")}
          </Cell>
        ) : null}
        <div style={{ padding: 8, paddingBottom: SAFE_AREA_BOTTOM }}>
          <Button
            mode="bezeled"
            size="l"
            stretched
            onClick={() => onSubmit(false)}
            disabled={createMutation.isPending}
            data-testid="newtrip-save"
          >
            {t("dispatcher:newtrip.save", "Save trip")}
          </Button>
        </div>
      </Section>
    </List>
  );
}
